/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { Modal } from "./Modal";
import { FormRow } from "../module/common/hook-form";
import { Select } from "antd";
import { SelectedItemBadge } from "./SelectedItemBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SpinnerBlock } from "../module/common/Spinner";
import { effects } from "./effects";
import { css } from "@emotion/react";
import { colors } from "./colors";
import { DropdownItemButton } from "../module/common/DropdownItemButton";
import { Divider } from "./Divider";
import { ButtonAction, ButtonPrimary } from "./NewButtons";
import { ActionSpinner } from "./ActionSpinner";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  options: Array<{ value: string; label: string }>;
  onSave: (selectedOptions: string[]) => void | Promise<void>;
  topComponent?: React.ReactNode;
  header: string;
  isLoadingSave?: boolean;
};
const buttonWrapperStyle = css`
  display: flex;
  align-items: center;
  margin-left: 8px;
  margin-right: 8px;
`;
const buttonWrapper = css`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: end;
`;

const dropdownMenuWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0px;
`;
const buttonAdditionalStyling = css`
  &:hover {
    background: ${colors.whiteHover};
  }
  width: 100%;
  padding: 5px 12px 5px 12px;
  border-radius: 4px;
`;
const dropdownInnerWrapperStyle = css`
  padding: 0px 8px;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const MultiSelectModal = ({
  isOpen,
  onClose,
  isLoading,
  options,
  onSave,
  topComponent,
  header,
  isLoadingSave,
}: Props) => {
  const [addedOptions, setAddedOptions] = useState<string[]>([]);

  const handleClose = () => {
    onClose();
    setAddedOptions([]);
  };

  return (
    <Modal header={header} isOpen={isOpen} onClose={handleClose}>
      {topComponent && topComponent}
      <FormRow>
        {isLoading ? (
          <SpinnerBlock />
        ) : (
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            tagRender={SelectedItemBadge}
            menuItemSelectedIcon={<FontAwesomeIcon icon={solid("check")} />}
            suffixIcon={<FontAwesomeIcon icon={solid("chevron-down")} />}
            value={addedOptions}
            optionFilterProp="label"
            allowClear
            dropdownStyle={{
              minWidth: "max-content",
              boxShadow: effects.popover,
              borderRadius: "4px",
              padding: "0px",
            }}
            onChange={(newValue: string[]) => {
              setAddedOptions(newValue);
            }}
            placeholder="Select options"
            options={options}
            maxTagCount="responsive"
            dropdownRender={(menu) => (
              <div css={dropdownMenuWrapperStyle}>
                <div css={buttonWrapperStyle}>
                  <DropdownItemButton
                    onClick={() => {
                      setAddedOptions((options || [])?.map((p) => p.value));
                    }}
                    type="button"
                    additionalStyles={buttonAdditionalStyling}
                    icon={<FontAwesomeIcon icon={solid("list-check")} />}
                  >
                    Select all
                  </DropdownItemButton>
                </div>
                <Divider />
                <div css={dropdownInnerWrapperStyle}>{menu}</div>
              </div>
            )}
          />
        )}
      </FormRow>
      <div css={buttonWrapper}>
        <ButtonPrimary onClick={() => onClose()} type="button">
          Cancel
        </ButtonPrimary>
        <ButtonAction
          onClick={async () => {
            await onSave(addedOptions);
            setAddedOptions([]);
            onClose();
          }}
          disabled={isLoadingSave}
          type="button"
        >
          {isLoadingSave ? <ActionSpinner /> : "Add options"}
        </ButtonAction>
      </div>
    </Modal>
  );
};
